<script setup lang="ts">
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
} from "ag-grid-community";
import { computed, nextTick, ref, watch } from "vue";
import AppGrid from "../../components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "../../components/composables/useGridColumnDefaults.ts";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";
import SuggestedOrdersColumnPicker from "./SuggestedOrdersColumnPicker.vue";
import {
  calculateSuggestedOrdersAaoRatio,
  createSuggestedOrdersDemoRows,
  SUGGESTED_ORDERS_COLUMNS_STORAGE_KEY,
  SUGGESTED_ORDERS_DEMO_DETAILS_COLUMNS,
  SUGGESTED_ORDERS_DEMO_ORDERS_COLUMNS,
  SUGGESTED_ORDERS_RESULTS_COLUMNS,
  SUGGESTED_ORDERS_RESULTS_DEFAULT_COLUMNS,
  type SuggestedOrdersDemoRecord,
  type SuggestedOrdersDemoRow,
} from "./suggestedOrdersDemo.ts";
import SuggestedOrdersFlowCell from "./SuggestedOrdersFlowCell.vue";

type DemoView = "results" | "orders" | "details";

const props = defineProps<{
  rows?: SuggestedOrdersDemoRow[];
  liveRows?: BulkOrderCreationRow[];
  /** Demand horizon (30/60/90 days) selecting the live ROS used for Weekly Sales. */
  rateOfSaleDays?: number;
}>();
const emit =
  defineEmits<(event: "update:rows", rows: SuggestedOrdersDemoRow[]) => void>();

const activeView = ref<DemoView>("results");
const gridApi = ref<GridApi<SuggestedOrdersDemoRow>>();
const ordersViewLabel = computed(() =>
  activeView.value === "results" ? "Results" : "Orders"
);
const demoRows = ref(
  props.rows ?? (props.liveRows ? [] : createSuggestedOrdersDemoRows())
);
// Live Order edits are local to this table; generation re-reads source quantities.
const liveOrderOverrides = ref(new Map<number, number>());
const liveRowIndices = new WeakMap<SuggestedOrdersDemoRow, number>();
const isLive = computed(() => props.liveRows !== undefined);
const primarySelections = ref(new Map<number, boolean>());
const preferenceError = ref("");
const visibleResultsColumns = ref<string[]>(readVisibleColumns());

function readVisibleColumns(): string[] {
  try {
    const stored = globalThis.localStorage.getItem(
      SUGGESTED_ORDERS_COLUMNS_STORAGE_KEY
    );
    if (stored === null) return [...SUGGESTED_ORDERS_RESULTS_DEFAULT_COLUMNS];
    const parsed: unknown = JSON.parse(stored);
    if (
      !Array.isArray(parsed) ||
      parsed.length === 0 ||
      !parsed.every(
        (column: unknown) =>
          typeof column === "string" &&
          SUGGESTED_ORDERS_RESULTS_COLUMNS.includes(column)
      )
    ) {
      throw new TypeError("The saved column selection is invalid.");
    }
    return SUGGESTED_ORDERS_RESULTS_COLUMNS.filter((column) =>
      parsed.includes(column)
    );
  } catch (error) {
    preferenceError.value = `Unable to read saved Results columns. Using defaults. ${error instanceof Error ? error.message : String(error)}`;
    return [...SUGGESTED_ORDERS_RESULTS_DEFAULT_COLUMNS];
  }
}

function selectVisibleColumns(columns: string[]): void {
  visibleResultsColumns.value = columns;
  try {
    globalThis.localStorage.setItem(
      SUGGESTED_ORDERS_COLUMNS_STORAGE_KEY,
      JSON.stringify(columns)
    );
    preferenceError.value = "";
  } catch (error) {
    preferenceError.value = `Results columns changed for this session, but could not be saved. ${error instanceof Error ? error.message : String(error)}`;
  }
}

function liveDailyRateOfSale(row: BulkOrderCreationRow): number | undefined {
  switch (props.rateOfSaleDays) {
    case 30: {
      return row.roS30;
    }
    case 60: {
      return row.roS60;
    }
    case 90: {
      return row.roS90;
    }
    default: {
      return undefined;
    }
  }
}

function roundTo(value: number, digits: number): number {
  return Number(value.toFixed(digits));
}

function toLiveRow(
  row: BulkOrderCreationRow,
  index: number
): SuggestedOrdersDemoRow {
  const order = liveOrderOverrides.value.get(index) ?? row.requestedUnits;
  const available = row.currentStock ?? null;
  const dailySales = liveDailyRateOfSale(row);
  const weeklySales = dailySales === undefined ? null : dailySales * 7;
  const aao = available === null ? null : available + order;
  // Workbook rule: New WOS = AAO / Weekly Sales, and 0 when Weekly Sales is 0.
  const newWos =
    aao === null || weeklySales === null
      ? null
      : weeklySales === 0
        ? 0
        : roundTo(aao / weeklySales, 2);
  const suggestedRow: SuggestedOrdersDemoRow = {
    sku: row.sku,
    orders: {
      SKU: row.sku,
      Description: row.description,
      Theme: row.theme ?? null,
      Available: available,
      "Weekly Sales": weeklySales === null ? null : roundTo(weeklySales, 4),
      "New WOS": newWos,
      Order: order,
      AAO: aao,
    },
    details: {
      SKU: row.sku,
      Color: row.color ?? null,
      Theme: row.theme ?? null,
      Description: row.description,
      Family: row.family ?? null,
      Cube: row.requestedUnits > 0 ? row.cubicFeet / row.requestedUnits : null,
      "Avg Daily Sales": dailySales ?? null,
      "Weekly Sales": weeklySales === null ? null : roundTo(weeklySales, 4),
      Available: available,
      Order: order,
      "Avail After Order": aao,
    },
  };
  liveRowIndices.set(suggestedRow, index);
  return suggestedRow;
}

const rows = computed<SuggestedOrdersDemoRow[]>(() =>
  props.liveRows
    ? props.liveRows.map((row, index) => toLiveRow(row, index))
    : demoRows.value
);
const primaryRow = computed(() =>
  rows.value.find((row) => row.orders.Primary === true)
);
const activeColumns = computed(() =>
  activeView.value === "details"
    ? SUGGESTED_ORDERS_DEMO_DETAILS_COLUMNS
    : activeView.value === "results"
      ? SUGGESTED_ORDERS_RESULTS_COLUMNS
      : SUGGESTED_ORDERS_DEMO_ORDERS_COLUMNS
);
const columnDefs = computed<ColDef<SuggestedOrdersDemoRow>[]>(() =>
  activeColumns.value.map((column) => ({
    headerName: column,
    colId: column,
    hide:
      activeView.value === "results" &&
      !visibleResultsColumns.value.includes(column),
    filter: column === "Flow" ? false : "agTextColumnFilter",
    sortable: column !== "Flow",
    ...(column === "Flow" ? { flex: 0, width: 56 } : {}),
    minWidth:
      column === "Flow"
        ? 56
        : column === "Description"
          ? 180
          : ["SKU", "Theme", "Family", "Color"].includes(column)
            ? 110
            : 90,
    ...(activeView.value === "results"
      ? column === "Description"
        ? { flex: 1, minWidth: 180 }
        : {
            flex: 0,
            width:
              column === "Primary"
                ? 90
                : column === "Flow"
                  ? 64
                  : column === "SKU"
                    ? 100
                    : 90,
            minWidth:
              column === "Primary"
                ? 90
                : column === "Flow"
                  ? 64
                  : column === "SKU"
                    ? 100
                    : 90,
          }
      : {}),
    valueGetter: ({ data }) => {
      if (!data) return null;
      if (column === "Primary")
        return (
          primarySelections.value.get(rows.value.indexOf(data)) ??
          data.orders.Primary === true
        );
      return (
        (activeView.value === "details"
          ? data.details[column]
          : data.orders[column]) ?? null
      );
    },
    cellRenderer:
      column === "Flow"
        ? SuggestedOrdersFlowCell
        : (parameters: ICellRendererParams<SuggestedOrdersDemoRow>) =>
            renderCell(column, parameters),
  }))
);

watch(
  () => props.rows,
  (updatedRows, previousRows) => {
    demoRows.value = updatedRows ?? [];
    if (
      updatedRows?.length !== previousRows?.length ||
      updatedRows?.some((row, index) => row.sku !== previousRows?.[index]?.sku)
    )
      primarySelections.value = new Map();
  }
);
watch(
  () => props.liveRows,
  () => {
    liveOrderOverrides.value = new Map();
    primarySelections.value = new Map();
  }
);
watch(activeView, () => {
  void nextTick(() => {
    gridApi.value?.setFilterModel(null);
  });
});

function onGridReady(event: GridReadyEvent<SuggestedOrdersDemoRow>): void {
  gridApi.value = event.api;
}

function getNumber(record: SuggestedOrdersDemoRecord, column: string): number {
  const value = record[column];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(
      `Suggested Orders demo row has an invalid numeric value in "${column}".`
    );
  }
  return value;
}

function formatCell(value: SuggestedOrdersDemoRecord[string]): string {
  if (value === null) return isLive.value ? "—" : "";
  if (typeof value === "boolean") return value ? "1" : "0";
  return String(value);
}

function formatAaoRatio(row: SuggestedOrdersDemoRow): string {
  const primary = primaryRow.value;
  if (!primary) {
    throw new TypeError("Suggested Orders demo must contain a Primary SKU.");
  }
  return calculateSuggestedOrdersAaoRatio(
    getNumber(row.orders, "AAO"),
    getNumber(primary.orders, "AAO")
  ).toFixed(2);
}

function createTextCell(value: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.textContent = value;
  return span;
}

function createNumberInput(
  label: string,
  value: number,
  minimum: number,
  maximum: number | undefined,
  onChange: (event: Event) => void
): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "number";
  input.className = "suggested-orders-demo-grid-input";
  input.min = String(minimum);
  if (maximum !== undefined) input.max = String(maximum);
  input.step = "1";
  input.setAttribute("aria-label", label);
  input.value = String(value);
  input.addEventListener("change", onChange);
  return input;
}

function renderCell(
  column: string,
  parameters: ICellRendererParams<SuggestedOrdersDemoRow>
): HTMLElement | string {
  const row = parameters.data;
  if (!row) return "";

  if (column === "Primary") {
    const input = document.createElement("input");
    input.type = "checkbox";
    input.className = "suggested-orders-primary-checkbox";
    input.setAttribute("aria-label", `Primary for SKU ${row.sku}`);
    const rowIndex = rows.value.indexOf(row);
    input.checked =
      primarySelections.value.get(rowIndex) ?? row.orders.Primary === true;
    // Selection flags intentionally do not replace the AAOR calculation baseline.
    input.addEventListener("change", () => {
      primarySelections.value.set(rowIndex, input.checked);
    });
    return input;
  }

  if (column === "Order") {
    if (isLive.value && activeView.value !== "details") {
      return createNumberInput(
        `Order for SKU ${row.sku}`,
        getNumber(row.orders, "Order"),
        0,
        undefined,
        (event) => {
          commitLiveOrder(row, liveRowIndices.get(row) ?? -1, event);
        }
      );
    }

    if (activeView.value === "details") {
      if (isLive.value) {
        return createTextCell(formatCell(row.details.Order ?? null));
      }

      return createNumberInput(
        `Details Order for SKU ${row.sku}`,
        getNumber(row.details, "Order"),
        0,
        999 - getNumber(row.details, "Available"),
        (event) => {
          commitOrder(row, event);
        }
      );
    }

    return createNumberInput(
      `Order for SKU ${row.sku}`,
      getNumber(row.orders, "Order"),
      0,
      999 - getNumber(row.orders, "Available"),
      (event) => {
        commitOrder(row, event);
      }
    );
  }

  if (
    column === "AAO" &&
    isLive.value &&
    activeView.value !== "details" &&
    row.orders.Available !== null
  ) {
    return createNumberInput(
      `AAO for SKU ${row.sku}`,
      getNumber(row.orders, "AAO"),
      getNumber(row.orders, "Available"),
      undefined,
      (event) => {
        commitLiveAao(row, liveRowIndices.get(row) ?? -1, event);
      }
    );
  }

  if (!isLive.value && column === "AAO" && activeView.value !== "details") {
    return createNumberInput(
      `AAO for SKU ${row.sku}`,
      getNumber(row.orders, "AAO"),
      getNumber(row.orders, "Available"),
      999,
      (event) => {
        commitAao(row, event);
      }
    );
  }

  const record = activeView.value === "details" ? row.details : row.orders;
  if (!isLive.value && activeView.value !== "details" && column === "AAOR") {
    return createTextCell(formatAaoRatio(row));
  }

  return createTextCell(formatCell(record[column] ?? null));
}

function rowsWithUpdatedQuantity(
  updatedRow: SuggestedOrdersDemoRow,
  quantity: number
): SuggestedOrdersDemoRow[] {
  const nextRows = rows.value.map((row) => {
    if (row.sku !== updatedRow.sku) return row;
    const available = getNumber(row.orders, "Available");
    const aao = available + quantity;
    return {
      ...row,
      orders: { ...row.orders, AAO: aao, Order: quantity },
      details: {
        ...row.details,
        Order: quantity,
        "Avail After Order": aao,
      },
    };
  });
  const primary = nextRows.find((row) => row.orders.Primary === true);
  if (!primary) {
    throw new TypeError("Suggested Orders demo must contain a Primary SKU.");
  }
  const primaryAao = getNumber(primary.orders, "AAO");
  return nextRows.map((row) => ({
    ...row,
    orders: {
      ...row.orders,
      AAOR: calculateSuggestedOrdersAaoRatio(
        getNumber(row.orders, "AAO"),
        primaryAao
      ),
    },
  }));
}

function updateRows(updatedRow: SuggestedOrdersDemoRow, quantity: number) {
  const nextRows = rowsWithUpdatedQuantity(updatedRow, quantity);
  demoRows.value = nextRows;
  emit("update:rows", nextRows);
}

function restoreInput(
  input: HTMLInputElement,
  record: SuggestedOrdersDemoRecord,
  column: string
): void {
  input.value = String(getNumber(record, column));
}

function commitOrder(row: SuggestedOrdersDemoRow, event: Event): void {
  const input = event.currentTarget;
  if (!(input instanceof HTMLInputElement)) return;

  const quantity = Number(input.value);
  const available = getNumber(row.orders, "Available");
  if (
    input.value.trim() === "" ||
    !Number.isSafeInteger(quantity) ||
    quantity < 0 ||
    quantity > 999 - available
  ) {
    restoreInput(input, row.orders, "Order");
    return;
  }

  updateRows(row, quantity);
}

function setLiveOrder(index: number, quantity: number): void {
  const overrides = new Map(liveOrderOverrides.value);
  overrides.set(index, quantity);
  liveOrderOverrides.value = overrides;
}

function commitLiveOrder(
  row: SuggestedOrdersDemoRow,
  index: number,
  event: Event
): void {
  const input = event.currentTarget;
  if (!(input instanceof HTMLInputElement)) return;

  const quantity = Number(input.value);
  if (
    input.value.trim() === "" ||
    !Number.isSafeInteger(quantity) ||
    quantity < 0
  ) {
    restoreInput(input, row.orders, "Order");
    return;
  }
  setLiveOrder(index, quantity);
}

function commitLiveAao(
  row: SuggestedOrdersDemoRow,
  index: number,
  event: Event
): void {
  const input = event.currentTarget;
  if (!(input instanceof HTMLInputElement)) return;

  const aao = Number(input.value);
  const available = getNumber(row.orders, "Available");
  if (
    input.value.trim() === "" ||
    !Number.isSafeInteger(aao) ||
    aao < available
  ) {
    restoreInput(input, row.orders, "AAO");
    return;
  }
  setLiveOrder(index, aao - available);
}

function commitAao(row: SuggestedOrdersDemoRow, event: Event): void {
  const input = event.currentTarget;
  if (!(input instanceof HTMLInputElement)) return;

  const aao = Number(input.value);
  const available = getNumber(row.orders, "Available");
  if (
    input.value.trim() === "" ||
    !Number.isSafeInteger(aao) ||
    aao < available ||
    aao > 999
  ) {
    restoreInput(input, row.orders, "AAO");
    return;
  }

  updateRows(row, aao - available);
}
</script>

<template>
  <section
    class="suggested-orders-demo"
    :aria-label="
      isLive
        ? 'Suggested Orders live results'
        : 'Suggested Orders workbook demo'
    "
  >
    <div
      class="suggested-orders-demo-view-switcher"
      role="group"
      :aria-label="
        isLive
          ? 'Suggested Orders live data views'
          : 'Suggested Orders demo data views'
      "
    >
      <button
        type="button"
        class="suggested-orders-demo-view-button"
        :aria-pressed="activeView === 'results'"
        @click="activeView = 'results'"
      >
        Results
      </button>
      <button
        type="button"
        class="suggested-orders-demo-view-button"
        :aria-pressed="activeView === 'orders'"
        @click="activeView = 'orders'"
      >
        Orders
      </button>
      <button
        type="button"
        class="suggested-orders-demo-view-button"
        :aria-pressed="activeView === 'details'"
        @click="activeView = 'details'"
      >
        Details
      </button>
      <SuggestedOrdersColumnPicker
        v-if="activeView === 'results'"
        :columns="SUGGESTED_ORDERS_RESULTS_COLUMNS"
        :visible-columns="visibleResultsColumns"
        @change="selectVisibleColumns"
        @reset="
          selectVisibleColumns([...SUGGESTED_ORDERS_RESULTS_DEFAULT_COLUMNS])
        "
      />
    </div>

    <p v-if="preferenceError" role="alert">{{ preferenceError }}</p>
    <div
      class="suggested-orders-demo-grid"
      role="region"
      :aria-label="`Suggested Orders ${ordersViewLabel} export`"
    >
      <AppGrid
        id="suggested-orders-results-grid"
        :column-defs="columnDefs"
        :row-data="rows"
        :default-col-def="FITTABLE_DEFAULT_COL_DEF"
        :auto-size-columns="false"
        :default-row-height="24"
        :get-row-height="() => 24"
        fill-container
        @grid-ready="onGridReady"
      />
    </div>
  </section>
</template>

<style scoped>
.suggested-orders-demo {
  display: flex;
  height: 100%;
  min-height: 0;
  min-width: 0;
  flex-direction: column;
  gap: 0.65rem;
}

.suggested-orders-demo-view-switcher {
  display: flex;
  flex-shrink: 0;
  gap: 0.35rem;
  border-bottom: 1px solid var(--border-subtle, #d9e0e8);
}

.suggested-orders-demo-view-button {
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--text-secondary, #5d6878);
  cursor: pointer;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0.45rem 0.75rem;
}

.suggested-orders-demo-view-button[aria-pressed="true"] {
  border-bottom-color: var(--action-primary, #1261a0);
  color: var(--action-primary, #1261a0);
}

.suggested-orders-demo-grid {
  display: flex;
  min-height: 0;
  min-width: 0;
  flex: 1 1 0;
  flex-direction: column;
  overflow: hidden;
}

/* stylelint-disable selector-pseudo-class-no-unknown */
.suggested-orders-demo-grid :deep(.suggested-orders-demo-grid-input) {
  width: 5.5rem;
  border: 1px solid var(--border-subtle, #b8c2cf);
  border-radius: 0.25rem;
  background: var(--surface-primary, #fff);
  color: inherit;
  font: inherit;
  height: 22px;
  line-height: 18px;
  padding: 1px 3px;
}

.suggested-orders-demo-grid
  :deep(.suggested-orders-demo-grid-input:focus-visible),
.suggested-orders-demo-grid
  :deep(.suggested-orders-primary-checkbox:focus-visible),
.suggested-orders-demo-view-button:focus-visible {
  outline: 2px solid var(--action-primary, #1261a0);
  outline-offset: 2px;
}
/* stylelint-enable selector-pseudo-class-no-unknown */
</style>
